import React, { useEffect, useState, useContext, useCallback } from "react";
import { AuthContext } from "utils/AuthContext";
import { FlexLeftCol } from "styles/global-style";

export default function Analytics() {
  const { supabase, user, roleAccess } = useContext(AuthContext);

  const [rows, setRows] = useState([]);

  const headerThStyle = {
    fontSize: 12,
    padding: "4px 8px",
    whiteSpace: "nowrap",
  };

  //----------------------------------------------------

  const fetchData = useCallback(async () => {
    // Only admins can view analytics; avoid unnecessary queries + guard against undefined auth state.
    if (!roleAccess?.("admin")) {
      setRows([]);
      return;
    }
    if (!user?.proj?.id) {
      setRows([]);
      return;
    }

    // New schema: tasks/columns have been replaced by the unified "work" table.
    // Tasks are work items with a parent_id and/or type === "task", and their workflow position is stored in `status`.
    const { data: workItems, error: workError } = await supabase
      .from("work")
      .select("id, estimate, status, owner_id, parent_id, type")
      .eq("project_id", user.proj.id);
    if (workError) console.error("Error fetching work:", workError);

    const taskItems = (workItems ?? []).filter((w) => w && (w.type === "task" || w.parent_id));
    if (taskItems.length === 0) {
      setRows([]);
      return;
    }

    // Group tasks by owner_id (assignee)
    const byOwner = taskItems.reduce((acc, t) => {
      if (!t?.owner_id) return acc;
      const ownerId = t.owner_id;
      if (!acc[ownerId]) {
        acc[ownerId] = {
          estimates: [],
          count: 0,
          sum: 0,
          byStatus: {
            TODO: { count: 0, sum: 0 },
            IN_PROGRESS: { count: 0, sum: 0 },
            REVIEW: { count: 0, sum: 0 },
            DONE: { count: 0, sum: 0 },
          },
        };
      }
      const status = t.status || "TODO";
      const bucket = acc[ownerId].byStatus[status] || (acc[ownerId].byStatus[status] = { count: 0, sum: 0 });
      bucket.count += 1;
      acc[ownerId].count += 1;
      if (typeof t.estimate === "number" && isFinite(t.estimate)) {
        bucket.sum += t.estimate;
        acc[ownerId].sum += t.estimate;
        acc[ownerId].estimates.push(t.estimate);
      }
      return acc;
    }, {});

    const ownerIds = Object.keys(byOwner);
    if (ownerIds.length === 0) {
      setRows([]);
      return;
    }

    // Fetch people data for user names
    const { data: peopleData, error: peopleError } = await supabase
      .from("people")
      .select("id, firstname, lastname, email")
      .in("id", ownerIds);
    if (peopleError) {
      console.error("Error fetching people:", peopleError);
    }

    // Compute average and median estimate per user (plus totals by status)
    const computed = Object.entries(byOwner).map(([personId, data]) => {
      const estimates = data.estimates || [];
      const avg = estimates.length ? estimates.reduce((sum, r) => sum + r, 0) / estimates.length : null;
      const sorted = estimates.slice().sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const med = sorted.length
        ? sorted.length % 2 !== 0
          ? sorted[mid]
          : (sorted[mid - 1] + sorted[mid]) / 2
        : null;
      const person = peopleData?.find((p) => String(p.id) === String(personId));
      const name =
        person && (person.firstname || person.lastname) ? `${person.firstname || ""} ${person.lastname || ""}`.trim() : person?.email || personId;
      return {
        personId,
        name,
        taskCount: data.count,
        totalEstimate: data.sum,
        averageEstimate: avg,
        medianEstimate: med,
        byStatus: data.byStatus,
      };
    });

    // Sort by total estimate desc
    computed.sort((a, b) => (b.totalEstimate || 0) - (a.totalEstimate || 0));

    setRows(computed);
  }, [supabase, user, roleAccess]);

  //----------------------------------------------------

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <FlexLeftCol>
      <h2>Analytics</h2>
      {roleAccess("admin") && rows.length > 0 && (
        <table>
          <thead>
            <tr style={{ fontWeight: "bold" }}>
              <th style={headerThStyle} rowSpan={2}>
                Name
              </th>
              <th style={headerThStyle} rowSpan={2}>
                Tasks
              </th>
              <th style={{ ...headerThStyle, textAlign: "center" }} colSpan={7}>
                Estimates
              </th>
            </tr>
            <tr style={{ fontWeight: "bold" }}>
              <th style={headerThStyle}>Total</th>
              <th style={headerThStyle}>Avg</th>
              <th style={headerThStyle}>Median</th>
              <th style={headerThStyle}>TODO</th>
              <th style={headerThStyle}>IN PROGRESS</th>
              <th style={headerThStyle}>REVIEW</th>
              <th style={headerThStyle}>DONE</th>
            </tr>
          </thead>
          <tbody style={{ textAlign: "center" }}>
            {rows.map((r) => (
              <tr key={r.personId}>
                <td>{r.name}</td>
                <td>{r.taskCount ?? 0}</td>
                <td>{(r.totalEstimate ?? 0).toFixed(1)}</td>
                <td>{typeof r.averageEstimate === "number" ? r.averageEstimate.toFixed(1) : "-"}</td>
                <td>{typeof r.medianEstimate === "number" ? r.medianEstimate.toFixed(1) : "-"}</td>
                <td>{(r.byStatus?.TODO?.sum ?? 0).toFixed(1)}</td>
                <td>{(r.byStatus?.IN_PROGRESS?.sum ?? 0).toFixed(1)}</td>
                <td>{(r.byStatus?.REVIEW?.sum ?? 0).toFixed(1)}</td>
                <td>{(r.byStatus?.DONE?.sum ?? 0).toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </FlexLeftCol>
  );
}
